/**
 * Learner-scoped API client (/api/learn/me/*).
 *
 * Deliberately NOT the shared axios `api` instance: that one is wired to the
 * studio Firebase app, the impersonation store, and the request coalescer.
 * Learner calls must carry a LEARN token and nothing else, so this is a thin
 * fetch wrapper instead.
 *
 * No endpoint here takes an actor: the server derives it from the token, which
 * is what makes it impossible to ask for someone else's data.
 */

import { getLearnIdToken } from './learnFirebase';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export interface LearnerProfile {
  id: string;
  email: string;
  displayName: string | null;
  photoUrl: string | null;
  actorId: string;
  createdAt: string;
  /** The school whose code this learner entered, if any. */
  school: { name: string } | null;
}

export interface LaunchTicket {
  actor: string;
  sig: string;
  expiresInSeconds: number;
  playUrl: string;
  directPlayUrl: string;
}

export interface AssessmentLaunchTicket {
  actor: string;
  sig: string;
  assessmentToken: string;
  /** The URL the server probed and wants this run opened at. Optional so an
   *  older api-server (pre-bake) still works — the client falls back. */
  runUrl?: string;
  baked?: boolean;
  appUrl?: string;
}

export type ProgressStatus = 'not_started' | 'in_progress' | 'completed';

export interface LessonProgress {
  shareToken: string;
  title: string | null;
  subject: string | null;
  grade: string | null;
  unit: string | null;
  courseId: string | null;
  chapter: string | null;
  thumbnailUrl: string | null;
  totalDuration: number;
  status: ProgressStatus;
  maxPercent: number;
  watchTimeMs: number;
  views: number;
  lastViewedAt: string | null;
  linkedTest: { taken: boolean; bestScorePercent: number | null; attempts: number } | null;
}

export interface LearnerStats {
  videosCompleted: number;
  videosInProgress: number;
  watchTimeMs: number;
  quizzesTaken: number;
  avgFirstAttemptScore: number | null;
}

export interface QuizAttempt {
  viewId: string;
  shareToken: string;
  title: string | null;
  startedAt: string | null;
  answered: number;
  correct: number;
  total: number | null;
  percent: number | null;
  durationMs: number | null;
  completed: boolean;
}

export class LearnerApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string
  ) {
    super(message);
    this.name = 'LearnerApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getLearnIdToken();
  if (!token) throw new LearnerApiError('Not signed in', 401, 'LEARNER_AUTH_REQUIRED');

  const res = await fetch(`${API_BASE_URL}/api/learn${path}`, {
    ...init,
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; code?: string };
    throw new LearnerApiError(body.error || 'Request failed', res.status, body.code);
  }
  return (await res.json()) as T;
}

export async function fetchLearnerProfile(): Promise<LearnerProfile> {
  const { learner } = await request<{ learner: LearnerProfile }>('/me');
  return learner;
}

/** Join a school. Works for an account still awaiting approval — a valid code
 *  is what approves it. Rejects with SCHOOL_CODE_INVALID / SCHOOL_ALREADY_SET. */
export async function redeemSchoolCode(code: string): Promise<{ name: string }> {
  const { school } = await request<{ school: { name: string } }>('/me/school-code', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
  return school;
}

export async function updateLearnerName(displayName: string): Promise<void> {
  await request('/me', { method: 'PATCH', body: JSON.stringify({ displayName }) });
}

export async function fetchMyProgress(): Promise<LessonProgress[]> {
  const { items } = await request<{ items: LessonProgress[] }>('/me/progress');
  return items;
}

export async function fetchMyStats(): Promise<LearnerStats> {
  const { stats } = await request<{ stats: LearnerStats }>('/me/stats');
  return stats;
}

export async function fetchMyQuizzes(): Promise<QuizAttempt[]> {
  const { attempts } = await request<{ attempts: QuizAttempt[] }>('/me/quizzes');
  return attempts;
}

/** Signed launch for one lesson. Minted per view — never cache across sessions. */
export function fetchLaunchTicket(shareToken: string): Promise<LaunchTicket> {
  return request<LaunchTicket>(`/me/launch/${encodeURIComponent(shareToken)}`);
}

export function fetchAssessmentLaunchTicket(
  shareToken: string,
  assessmentToken: string
): Promise<AssessmentLaunchTicket> {
  return request<AssessmentLaunchTicket>(
    `/me/launch/${encodeURIComponent(shareToken)}/assessment/${encodeURIComponent(assessmentToken)}`
  );
}

// ---------------------------------------------------------------------------
// The journey — a lesson with everything it produced
// ---------------------------------------------------------------------------

export interface WatchInstance {
  viewId: string;
  startedAt: string | null;
  maxPercent: number;
  watchTimeMs: number;
  completed: boolean;
}

export interface CheckInstance {
  viewId: string;
  assessmentToken: string;
  startedAt: string | null;
  percent: number | null;
  correct: number | null;
  total: number | null;
  durationMs: number | null;
  finished: boolean;
}

export interface JourneyLesson {
  shareToken: string;
  title: string | null;
  subject: string | null;
  grade: string | null;
  chapter: string | null;
  courseId: string | null;
  thumbnailUrl: string | null;
  watches: WatchInstance[];
  checks: CheckInstance[];
}

export async function fetchMyJourney(): Promise<JourneyLesson[]> {
  const { lessons } = await request<{ lessons: JourneyLesson[] }>('/me/journey');
  return lessons ?? [];
}

// ---------------------------------------------------------------------------
// What this learner has been assigned
// ---------------------------------------------------------------------------

export interface LearnerAccess {
  mode: 'ALL' | 'ASSIGNED';
  courseIds: string[];
  lessonTokens: string[];
  /** null = everything. An EMPTY ARRAY means assigned nothing — not the same. */
  visibleTokens: string[] | null;
  visibleCourseIds: string[] | null;
}

export async function fetchMyAccess(): Promise<LearnerAccess> {
  return request<LearnerAccess>('/me/access');
}
